import { apiClient } from '@core/api/ApiClientFactory';
import { DateTime } from 'luxon';
import { CountCapacityData, GatewayStatus, Shop, TrolleyShelterData } from '@core/types';

// ============ TYPES ============

export interface ShopStatusData {
    id: string;
    name: string;
    isProduction: boolean;
    status: {
        healthStatusType: string;
        changeDate: string | null;
        isOnline: boolean;
    };
    unhealthy: string[];
    gateways?: GatewayStatus[];
}

export interface TrolleyShelterCountCapacity {
    currentCount: number;
    capacity: number;
}

export interface ZoneEntriesExitsToday {
    deviceId: string;
    moduleId: string;
    entries: number;
    exits?: number;
    currentBalance?: number;
}

export interface ZoneFillLevelLast4Hours {
    intervalStart: string;
    max: number;
    avg: number;
    min: number;
}

export interface ZoneEntryData {
    deviceId: string;
    moduleId: string;
    entries: number;
    exits?: number;
    currentBalance?: number;
}

export interface ZoneFillData {
    intervalStart: string;
    max: number;
    avg: number;
    min: number;
}

export interface FastLanerTrolleyData {
    data: Array<{
        trolleys: Array<{
            batteryState: string;
            isCharging: boolean;
            healthStatus: {
                wlan: string;
                hybridLoc: string;
                scanner: string;
                camera: string;
            };
        }>;
    }>;
}

// ============ SERVICE IMPLEMENTATION ============

export const dashboardService = {
    // Shop Status Methods
    async loadShopsStatus(): Promise<ShopStatusData[]> {
        try {
            console.log('[DashboardService] Loading all shops status...');
            const response = await apiClient.get<ShopStatusData[]>('shop/status');
            console.log('[DashboardService] Shops status loaded:', response.data);
            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading shops status:', error);
            throw error;
        }
    },

    // Trolley Shelter Methods
    async loadTrolleyShelterCountCapacity({
        deviceId,
        moduleId
    }: {
        deviceId: string;
        moduleId: string;
    }): Promise<CountCapacityData> {
        try {
            const response = await apiClient.get<CountCapacityData>(
                `statistics/shelterfilllevel/${deviceId}/${moduleId}`
            );
            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading trolley shelter count capacity:', error);
            throw error;
        }
    },

    async loadTrolleySheltersCountCapacity(shopId: string): Promise<TrolleyShelterData[]> {
        try {
            const response = await apiClient.get<TrolleyShelterData[]>(`statistics/shelterfilllevel/${shopId}`);
            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading trolley shelters count capacity:', error);
            throw error;
        }
    },

    // Zone Tracker Methods
    async loadZoneEntriesExitsToday(shopId: string): Promise<ZoneEntryData[]> {
        try {
            const now = DateTime.now();
            const zonedNow = now.setZone('UTC').plus({ minutes: now.offset });
            const start = zonedNow.startOf('day').toISO();
            const end = zonedNow.toISO();

            const response = await apiClient.get<ZoneEntryData[]>(
                `statistics/zonetracker/zoneentriesexitstoday/${shopId}/${start}/${end}`
            );
            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading zone entries/exits today:', error);
            throw error;
        }
    },

    async loadZoneFillLevelLast4Hours({
        deviceId,
        moduleId
    }: {
        deviceId: string;
        moduleId: string;
    }): Promise<ZoneFillData[]> {
        try {
            const now = DateTime.now();
            const zonedNow = now.setZone('UTC').plus({ minutes: now.offset });
            const start = zonedNow.minus({ hours: 4 }).toISO();
            const end = zonedNow.toISO();

            const response = await apiClient.get<ZoneFillData[]>(
                `statistics/zonetracker/${deviceId}/${moduleId}/${start}/${end}/30`
            );
            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading zone fill level last 4 hours:', error);
            throw error;
        }
    },

    // FastLaner Methods
    async getFastLanerTrolleyData(fastLanerGroupIds?: string[]): Promise<FastLanerTrolleyData | null> {
        try {
            if (!fastLanerGroupIds) {
                return null;
            }

            const params = new URLSearchParams();
            fastLanerGroupIds.forEach((id) => params.append('groupId', id));

            const response = await apiClient.get<FastLanerTrolleyData>(`fastlaner/trolley-data?${params}`);

            return response.data;
        } catch (error) {
            console.error('[DashboardService] Error loading FastLaner trolley data:', error);
            throw error;
        }
    }
};
